import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Spinner,
  makeStyles,
} from '@fluentui/react-components';
import { useAuthStore } from '../stores/authStore.js';
import { qaBypassSubscription, me } from '../api/auth.js';

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: 'var(--space-m)' },
  caution: {
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
  },
});

/**
 * Modal exclusivo de **entornos de pruebas**.
 *
 * ⚠️ HERRAMIENTA DE DESARROLLO — NO EXISTE EN PRODUCCIÓN.
 *
 * Aparece post-login cuando:
 *   - `qaModeEnabled === true` (`env.ENABLE_QA_MODE` + admin/owner ECOSOFT).
 *   - `subscriptionExpired === true` (Anywhere reportó fecha vencida).
 *
 * En producción `ENABLE_QA_MODE=false` → el backend nunca setea
 * `qaModeEnabled=true` → este componente nunca abre el modal aunque
 * esté montado. Doble gate: env var + role.
 *
 * Pregunta al tester si quiere operar:
 *   - **Como cliente vencido**: deja el flujo real (modo lectura, banner
 *     rojo, mutations bloqueadas). Útil para validar la UX que vería el
 *     cliente real.
 *   - **Como tester sin restricciones**: llama al endpoint
 *     `/auth/qa-bypass-subscription` con bypass=true → re-emite JWT
 *     sin el claim `subscriptionExpiresAt` → resto de la sesión normal.
 *
 * Cuando el usuario elige una opción, no vuelve a aparecer hasta el
 * próximo login (el JWT trae el estado correcto).
 */
export function QaSubscriptionModeGate() {
  const styles = useStyles();
  const qaModeEnabled = useAuthStore((s) => s.qaModeEnabled);
  const subscriptionExpired = useAuthStore((s) => s.subscriptionExpired);
  const status = useAuthStore((s) => s.status);
  const setSession = useAuthStore((s) => s.setSession);

  const [open, setOpen] = useState(false);
  const [askedThisSession, setAskedThisSession] = useState(false);
  const [busy, setBusy] = useState<'expired' | 'tester' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Reset state cuando el user se desloguea — el componente NO se
  // desmonta en logout (vive arriba de <Routes>), así que sin este
  // efecto el `askedThisSession=true` persiste a sesiones nuevas y
  // el modal nunca se vuelve a mostrar.
  useEffect(() => {
    if (status === 'unauthenticated') {
      setAskedThisSession(false);
      setOpen(false);
    }
  }, [status]);

  useEffect(() => {
    if (qaModeEnabled && subscriptionExpired && !askedThisSession) {
      setOpen(true);
    }
  }, [qaModeEnabled, subscriptionExpired, askedThisSession]);

  function chooseExpired() {
    setAskedThisSession(true);
    setOpen(false);
  }

  async function chooseTester() {
    setBusy('tester');
    setErr(null);
    try {
      await qaBypassSubscription(true);
      // Recargar /me para refrescar el authStore (subscriptionExpired → false).
      const refreshed = await me();
      setSession(refreshed);
      setAskedThisSession(true);
      setOpen(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  if (!open) return null;

  return (
    <Dialog open modalType="modal">
      <DialogSurface>
        <DialogBody>
          <DialogTitle>¿Cómo quieres operar esta sesión?</DialogTitle>
          <DialogContent className={styles.body}>
            <MessageBar intent="info" layout="multiline">
              <MessageBarBody>
                <strong>Diálogo de pruebas</strong> — sólo visible en entornos dev/staging.
                En producción jamás aparece.
              </MessageBarBody>
            </MessageBar>

            <p>
              Detectamos que tu licencia OPUS 27 está vencida. Como eres staff Ecosoft,
              puedes elegir cómo continuar:
            </p>

            <ul style={{ paddingLeft: 18, margin: 0, lineHeight: 1.7 }}>
              <li>
                <strong>Modo cliente vencido</strong>: experiencia real de un cliente con
                sub vencida — modo lectura, banner rojo, mutations bloqueadas. Útil para
                validar la UX del cliente.
              </li>
              <li>
                <strong>Modo tester</strong>: re-emite tu sesión sin la marca de
                vencimiento. Operas normal, sin restricciones. Tu sesión actual no
                refleja la experiencia del cliente real.
              </li>
            </ul>

            {err && (
              <MessageBar intent="error">
                <MessageBarBody>{err}</MessageBarBody>
              </MessageBar>
            )}
            <span className={styles.caution}>
              La elección dura mientras la sesión esté activa. Para volver a elegir,
              cierra sesión y vuelve a entrar.
            </span>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" disabled={busy !== null} onClick={chooseExpired}>
              Continuar como cliente vencido
            </Button>
            <Button
              appearance="primary"
              disabled={busy !== null}
              icon={busy === 'tester' ? <Spinner size="extra-tiny" /> : undefined}
              onClick={() => void chooseTester()}
            >
              Continuar como tester (sin restricciones)
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
